import BiobankData from 'BrdData/BiobankData';
import { getObjectStatus, OBJECT_STATUS, user_roles } from 'BrdData/Enum';
import {
  BooleanAndNotesComponent,
  ContentWithNotesComponent,
  HSCNoWrapText,
  StatusLabel,
} from 'Components';
import {
  buildSequelizeRestParams,
  DataTable,
  DataTableColumnHeader,
  DataTableHeaderSwitchFilter,
  DataTableHeaderTextFieldFilter,
  SequelizeFilterHelper,
} from 'Components/DataTable';
import { AddNewButton, HSCButton, QuickViewButton } from 'Components/Buttons';
import { createColumnHelper } from '@tanstack/react-table';

import { SITE_PAGES } from 'Config';
import { useDrawerContext } from 'Context';
import { DataManager } from 'Lib/data';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { getClickableLink, isUserAuthorized } from 'Util';
import BiobankDetailsView from './BiobankDetailsView';
import BiobankDrawerButton from './BiobankDrawerButton';
import BiobankLanding from './BiobankLanding';

const { NUMBER_LIKE_FILTER_MAP, TEXT_LIKE_FILTER_MAP } = SequelizeFilterHelper;

let DM = new DataManager();

const columnHelper = createColumnHelper();

const BiobankListView = () => {
  const navigate = useNavigate();
  const { openDrawer, closeDrawer } = useDrawerContext();
  const [refreshKey, setRefreshKey] = useState(0);
  const breadcrumb = [SITE_PAGES.BIOBANK_LANDING];

  useEffect(() => {
    DM.add(
      BiobankData.registerListener('RECORD_CREATED', refreshAndCloseDrawer),
      BiobankData,
    );
    DM.add(
      BiobankData.registerListener('RECORD_UPDATED', refreshAndCloseDrawer),
      BiobankData,
    );
    DM.add(
      BiobankData.registerListener('RECORD_ACTIVATED', refreshAndCloseDrawer),
      BiobankData,
    );
    DM.add(
      BiobankData.registerListener('RECORD_DEACTIVATED', refreshAndCloseDrawer),
      BiobankData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  const refreshData = () => {
    setRefreshKey((prevState) => prevState + 1);
  };

  const refreshAndCloseDrawer = () => {
    refreshData();
    closeDrawer();
  };

  let drawerContent = <BiobankDetailsView compact editable hideToggle isNew />;

  const onClick = () => {
    openDrawer({
      content: drawerContent,
      title: 'New Bio Resource Entry',
      size: { defaultWidth: 680, minWidth: 580 },
    });
  };

  let addNewButton = isUserAuthorized({
    roles: [user_roles.SUPER_EDITOR, user_roles.EDITOR],
  }) && <AddNewButton buttonText='Add Bio Resource Entry' onClick={onClick} />;

  const isInactive = (bb) => {
    return bb?.status === OBJECT_STATUS.DEACTIVATED.key || false;
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.institution, {
        id: 'institution',
        size: 100,
        meta: {
          label: 'Inst',
          sortable: true,
          filterFieldName: '*institution',
          mapFilterValue: TEXT_LIKE_FILTER_MAP,
        },
        header: ({ column, table }) => (
          <DataTableColumnHeader column={column} table={table} label='Inst'>
            <DataTableHeaderTextFieldFilter
              fieldName={'Inst.'}
              defaultValue={column.getFilterValue() ?? ''}
              onFilterValueUpdate={(e) => column.setFilterValue(e.target.value)}
            />
          </DataTableColumnHeader>
        ),
      }),

      columnHelper.accessor((row) => row.indication, {
        id: 'indication',
        meta: {
          label: 'Indication',
          sortable: true,
          filterFieldName: '*indication',
          mapFilterValue: TEXT_LIKE_FILTER_MAP,
        },
        header: ({ column, table }) => (
          <DataTableColumnHeader
            column={column}
            table={table}
            label='Indication'>
            <DataTableHeaderTextFieldFilter
              fieldName={'Indication'}
              defaultValue={column.getFilterValue() ?? ''}
              onFilterValueUpdate={(e) => column.setFilterValue(e.target.value)}
            />
          </DataTableColumnHeader>
        ),
      }),

      columnHelper.accessor((row) => row.in_scope_notes, {
        id: 'in_scope_notes',
        size: 104,
        minSize: 100,
        maxSize: 116,
        meta: {
          label: 'In Scope',
          centeredHeader: true,
          filterFieldName: '*in_scope_notes',
          mapFilterValue: TEXT_LIKE_FILTER_MAP,
          getText: (bb) =>
            `${!!bb.in_scope}${
              bb.in_scope_notes ? ' | ' + bb.in_scope_notes : ' '
            }`,
        },
        header: ({ column, table }) => (
          <DataTableColumnHeader column={column} table={table} label='In Scope'>
            <DataTableHeaderTextFieldFilter
              fieldName={'In Scope (Notes)'}
              defaultValue={column.getFilterValue() ?? ''}
              onFilterValueUpdate={(e) => column.setFilterValue(e.target.value)}
            />
          </DataTableColumnHeader>
        ),
        cell: ({ row }) => (
          <BooleanAndNotesComponent
            isTrue={row.original.in_scope}
            notes={row.original.in_scope_notes}
          />
        ),
      }),

      columnHelper.accessor((row) => row.num_individuals, {
        id: 'num_individuals',
        size: 60,
        maxSize: 80,
        meta: {
          label: 'N',
          filterFieldName: '*num_individuals',
          mapFilterValue: NUMBER_LIKE_FILTER_MAP,
          getText: (bb) =>
            `${bb.num_individuals}${
              bb.num_individuals_notes ? ' | ' + bb.num_individuals_notes : ''
            }`,
        },
        header: ({ column, table }) => (
          <DataTableColumnHeader column={column} table={table} label='N'>
            <DataTableHeaderTextFieldFilter
              fieldName={'N'}
              defaultValue={column.getFilterValue() ?? ''}
              onFilterValueUpdate={(e) => column.setFilterValue(e.target.value)}
            />
          </DataTableColumnHeader>
        ),
        cell: ({ row }) =>
          (row.original.num_individuals ||
            row.original.num_individuals_notes) && (
            <ContentWithNotesComponent
              content={row.original.num_individuals}
              notes={row.original.num_individuals_notes}
            />
          ),
      }),

      columnHelper.accessor((row) => row.molecular_profiling_notes, {
        id: 'molecular_profiling_notes',
        size: 80,
        maxSize: 100,
        meta: {
          label: 'Mol Prof',
          centeredHeader: true,
          filterFieldName: '*molecular_profiling_notes',
          mapFilterValue: TEXT_LIKE_FILTER_MAP,
          getText: (bb) =>
            `${!!bb.molecular_profiling}${
              bb.molecular_profiling_notes
                ? ' | ' + bb.molecular_profiling_notes
                : ' '
            }`,
        },
        header: ({ column, table }) => (
          <DataTableColumnHeader column={column} table={table} label='Mol Prof'>
            <DataTableHeaderTextFieldFilter
              fieldName={'Molecular Profiling (Notes)'}
              defaultValue={column.getFilterValue() ?? ''}
              onFilterValueUpdate={(e) => column.setFilterValue(e.target.value)}
            />
          </DataTableColumnHeader>
        ),
        cell: ({ row }) => (
          <BooleanAndNotesComponent
            isTrue={row.original.molecular_profiling}
            notes={row.original.molecular_profiling_notes}
          />
        ),
      }),

      columnHelper.accessor((row) => row.biospecimens, {
        id: 'biospecimens',
        meta: {
          label: 'Biospecimens',
          filterFieldName: '*biospecimens',
          mapFilterValue: TEXT_LIKE_FILTER_MAP,
        },
        header: ({ column, table }) => (
          <DataTableColumnHeader
            column={column}
            table={table}
            label='Biospecimens'>
            <DataTableHeaderTextFieldFilter
              fieldName={'Biospecimens'}
              defaultValue={column.getFilterValue() ?? ''}
              onFilterValueUpdate={(e) => column.setFilterValue(e.target.value)}
            />
          </DataTableColumnHeader>
        ),
        cell: ({ getValue }) => (
          <HSCNoWrapText tooltipText={'Click to see entire entry'}>
            {getValue()}
          </HSCNoWrapText>
        ),
      }),

      columnHelper.accessor((row) => row.investigator, {
        id: 'investigator',
        meta: {
          label: 'Investigator(s)',
          filterFieldName: '*investigator',
          mapFilterValue: TEXT_LIKE_FILTER_MAP,
        },
        header: ({ column, table }) => (
          <DataTableColumnHeader
            column={column}
            table={table}
            label='Investigator(s)'>
            <DataTableHeaderTextFieldFilter
              fieldName={'Investigator(s)'}
              defaultValue={column.getFilterValue() ?? ''}
              onFilterValueUpdate={(e) => column.setFilterValue(e.target.value)}
            />
          </DataTableColumnHeader>
        ),
      }),

      columnHelper.accessor((row) => row.department, {
        id: 'department',
        meta: {
          label: 'Department(s)',
          filterFieldName: '*department',
          mapFilterValue: TEXT_LIKE_FILTER_MAP,
        },
        header: ({ column, table }) => (
          <DataTableColumnHeader
            column={column}
            table={table}
            label='Department(s)'>
            <DataTableHeaderTextFieldFilter
              fieldName={'Department(s)'}
              defaultValue={column.getFilterValue() ?? ''}
              onFilterValueUpdate={(e) => column.setFilterValue(e.target.value)}
            />
          </DataTableColumnHeader>
        ),
        cell: ({ getValue }) => (
          <HSCNoWrapText tooltipText={'Click to see entire entry'}>
            {getValue()}
          </HSCNoWrapText>
        ),
      }),

      columnHelper.accessor((row) => row.comments, {
        id: 'comments',
        meta: {
          label: 'Comments',
          filterFieldName: '*comments',
          mapFilterValue: TEXT_LIKE_FILTER_MAP,
        },
        header: ({ column, table }) => (
          <DataTableColumnHeader column={column} table={table} label='Comments'>
            <DataTableHeaderTextFieldFilter
              fieldName={'Comments'}
              defaultValue={column.getFilterValue() ?? ''}
              onFilterValueUpdate={(e) => column.setFilterValue(e.target.value)}
            />
          </DataTableColumnHeader>
        ),
        cell: ({ getValue }) => (
          <HSCNoWrapText tooltipText={'Click to see entire entry'}>
            {getValue()}
          </HSCNoWrapText>
        ),
      }),

      columnHelper.accessor((row) => row.link, {
        id: 'link',
        size: 60,
        minSize: 50,
        meta: { label: 'Link', centeredHeader: true, isIconCol: true },
        header: ({ column, table }) => (
          <DataTableColumnHeader column={column} table={table} label='Link' />
        ),
        cell: ({ getValue }) =>
          getValue() && (
            <HSCButton
              iconOnly
              minimal
              href={getClickableLink(getValue())}
              target='_blank'
              tooltipText={getValue()}
              iconName={'share'}
            />
          ),
      }),

      columnHelper.accessor((row) => row.status, {
        id: 'status',
        size: 80,
        maxSize: 80,
        meta: {
          label: 'Status',
          centeredHeader: true,
          filterFieldName: '*status',
          getText: (bb) => getObjectStatus(bb.status)?.label || bb.status,
        },
        header: ({ column, table }) => (
          <DataTableColumnHeader
            column={column}
            table={table}
            label='Status'
            onOpenFilter={() => {
              if (!column.getFilterValue()?.length) {
                column.setFilterValue([OBJECT_STATUS.ACTIVE]);
              }
            }}>
            <DataTableHeaderSwitchFilter
              fieldName={'Is Active'}
              onFilterValueUpdate={(val) =>
                column.setFilterValue(
                  val ? [OBJECT_STATUS.ACTIVE] : [OBJECT_STATUS.DEACTIVATED],
                )
              }
              checked={
                column.getFilterValue()?.length
                  ? column.getFilterValue()[0] === OBJECT_STATUS.ACTIVE
                  : true
              }
              isStatusSwitch
            />
          </DataTableColumnHeader>
        ),
        cell: ({ getValue }) => <StatusLabel soft statusKey={getValue()} />,
      }),

      columnHelper.display({
        id: 'quick_view',
        size: 36,
        minSize: 36,
        maxSize: 36,
        enableResizing: false,
        enableHiding: false,
        meta: { excludeFromExport: true, isIconCol: true },
        header: () => '',
        cell: ({ row }) => {
          const bb = row.original;
          return (
            <BiobankDrawerButton
              editable={isUserAuthorized({ roles: [user_roles.SUPER_EDITOR] })}
              biobankId={bb.id}
              label={<QuickViewButton />}
              drawerTitle={`${bb.investigator}-${bb.indication}`}
              linkToDetailId={bb.id}
            />
          );
        },
      }),
    ],
    [],
  );

  const queryFn = (params) =>
    BiobankData.getBiobankList(
      buildSequelizeRestParams({ columns, ...params, defaultSort: 'id' }),
    ).then(({ records, count }) => ({ rows: records, rowCount: count }));

  const exportQueryFn = (params) =>
    BiobankData.exportList(
      buildSequelizeRestParams({ columns, ...params, defaultSort: 'id' }),
    );

  return (
    <BiobankLanding breadcrumbs={breadcrumb}>
      <DataTable
        tableId={'BB'}
        columns={columns}
        queryFn={queryFn}
        exportQueryFn={exportQueryFn}
        refetchToken={refreshKey}
        initialSorting={[{ id: 'id', desc: false }]}
        title={'Bio Resource Entries'}
        actionButtons={addNewButton}
        highlightFunc={isInactive}
        defaultColumnVisibility={{ status: false }}
        onRowClick={(record) =>
          navigate(`${SITE_PAGES.BIOBANK_DETAIL.path}/${record.id}`)
        }
      />
    </BiobankLanding>
  );
};

BiobankListView.propTypes = {};

export default BiobankListView;
